import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '@/App';
import Navbar from '@/components/Navbar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  MapPin, Phone, Globe, Star, Truck, Leaf, Search,
  Filter, ChevronRight, Loader2, ShoppingBag
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const Producers = () => {
  const { API } = useAuth();
  const [loading, setLoading] = useState(false);
  const [producers, setProducers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [counties, setCounties] = useState([]);
  
  // Filters
  const [selectedCounty, setSelectedCounty] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Selected producer details
  const [selectedProducer, setSelectedProducer] = useState(null);

  useEffect(() => {
    fetchProducers();
    fetchCategories();
    fetchCounties();
  }, []);

  useEffect(() => {
    fetchProducers();
  }, [selectedCounty, selectedCategory]);

  const fetchProducers = async () => {
    setLoading(true);
    try {
      let url = `${API}/producers?page=1`;
      if (selectedCounty) url += `&county=${selectedCounty}`;
      if (selectedCategory) url += `&category=${selectedCategory}`;
      
      const response = await axios.get(url);
      setProducers(response.data.producers || []);
    } catch (error) {
      console.error('Error fetching producers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/producers/categories`);
      setCategories(response.data.categories || []);
    } catch (error) {
      // Default categories
      setCategories([
        { id: 'legume', name: 'Legume', icon: '🥬' },
        { id: 'fructe', name: 'Fructe', icon: '🍎' },
        { id: 'lactate', name: 'Lactate', icon: '🧀' },
        { id: 'carne', name: 'Carne', icon: '🥩' },
        { id: 'miere', name: 'Miere', icon: '🍯' },
        { id: 'bio', name: 'Bio', icon: '🌱' }
      ]);
    }
  };

  const fetchCounties = async () => {
    try {
      const response = await axios.get(`${API}/counties`);
      setCounties(response.data.counties || []);
    } catch (error) {
      console.error('Error fetching counties:', error);
    }
  };

  const filteredProducers = producers.filter(p => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return p.name.toLowerCase().includes(query) || 
             p.products?.some(prod => prod.toLowerCase().includes(query));
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-[#F5F5F0]">
      <Navbar />

      <div className="container mx-auto px-6 md:px-12 py-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="text-center mb-8">
            <h1 className="font-serif text-4xl md:text-5xl font-light text-[#1A4D2E] mb-2">
              Producători Locali
            </h1>
            <p className="text-[#4a5568] text-lg">
              Descoperă și susține producătorii locali din România
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            {/* Search and Filters */}
            <Card className="border-none card-shadow mb-8">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row gap-4">
                  {/* Search */}
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4a5568]" />
                    <Input
                      placeholder="Caută producători sau produse..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 bg-[#F5F5F0] border-transparent focus:border-[#1A4D2E] h-12"
                    />
                  </div>
                  
                  {/* County Filter */}
                  <select
                    value={selectedCounty}
                    onChange={(e) => setSelectedCounty(e.target.value)}
                    className="h-12 px-4 rounded-lg bg-[#F5F5F0] border-transparent text-[#4a5568] min-w-[180px]"
                  >
                    <option value="">Toate județele</option>
                    {counties.map((county) => (
                      <option key={county} value={county}>{county}</option>
                    ))}
                  </select>
                </div>

                {/* Category Pills */}
                <div className="flex flex-wrap gap-2 mt-4">
                  <Button
                    size="sm"
                    variant={!selectedCategory ? 'default' : 'outline'}
                    onClick={() => setSelectedCategory('')}
                    className={!selectedCategory ? 'bg-[#1A4D2E]' : 'border-[#1A4D2E]/30'}
                  >
                    Toate
                  </Button>
                  {categories.map((cat) => (
                    <Button
                      key={cat.id}
                      size="sm"
                      variant={selectedCategory === cat.id ? 'default' : 'outline'}
                      onClick={() => setSelectedCategory(cat.id)}
                      className={selectedCategory === cat.id ? 'bg-[#1A4D2E]' : 'border-[#1A4D2E]/30'}
                    >
                      {cat.icon} {cat.name}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Producers Grid */}
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
              </div>
            ) : (
              <>
                <p className="text-[#4a5568] mb-4">{filteredProducers.length} producători găsiți</p>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredProducers.map((producer) => (
                    <Card 
                      key={producer.id} 
                      className="border-none card-shadow hover:shadow-lg transition-all cursor-pointer overflow-hidden"
                      onClick={() => setSelectedProducer(producer)}
                    >
                      {producer.image && (
                        <div className="h-48 overflow-hidden">
                          <img 
                            src={producer.image} 
                            alt={producer.name}
                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                      )}
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-serif text-xl text-[#1A4D2E]">{producer.name}</h3>
                          {producer.rating && (
                            <Badge className="bg-yellow-100 text-yellow-800 flex items-center">
                              <Star className="w-3 h-3 mr-1 fill-current" />
                              {producer.rating}
                            </Badge>
                          )}
                        </div>
                        
                        <p className="text-sm text-[#4a5568] mb-3 line-clamp-2">{producer.description}</p>
                        
                        <div className="flex items-center text-sm text-[#4a5568] mb-3">
                          <MapPin className="w-4 h-4 mr-1" />
                          {producer.city}, {producer.county}
                        </div>
                        
                        {/* Categories */}
                        <div className="flex flex-wrap gap-1 mb-3">
                          {producer.categories?.slice(0, 3).map((cat, i) => (
                            <Badge key={i} className="bg-[#1A4D2E]/10 text-[#1A4D2E] text-xs">{cat}</Badge>
                          ))}
                        </div>
                        
                        {/* Products Preview */}
                        {producer.products?.length > 0 && (
                          <p className="text-xs text-[#4a5568] line-clamp-1">
                            🛒 {producer.products.slice(0, 3).join(', ')}
                            {producer.products.length > 3 && ` +${producer.products.length - 3}`}
                          </p>
                        )}
                        
                        <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#E2E8F0]">
                          <div className="flex items-center gap-2">
                            {producer.delivery && (
                              <Badge variant="outline" className="text-xs border-green-300 text-green-700">
                                <Truck className="w-3 h-3 mr-1" />
                                Livrare
                              </Badge>
                            )}
                            {producer.certifications?.includes('Bio certificat EU') && (
                              <Badge variant="outline" className="text-xs border-green-300 text-green-700">
                                <Leaf className="w-3 h-3 mr-1" />
                                Bio
                              </Badge>
                            )}
                          </div>
                          <ChevronRight className="w-5 h-5 text-[#1A4D2E]" />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </>
            )}

            {/* Producer Details Modal */}
            {selectedProducer && (
              <div 
                className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
                onClick={() => setSelectedProducer(null)}
              >
                <motion.div 
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  {selectedProducer.image && (
                    <div className="h-64 overflow-hidden">
                      <img 
                        src={selectedProducer.image} 
                        alt={selectedProducer.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  
                  <div className="p-8">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h2 className="font-serif text-3xl text-[#1A4D2E] mb-1">{selectedProducer.name}</h2>
                        <div className="flex items-center text-[#4a5568]">
                          <MapPin className="w-4 h-4 mr-1" />
                          {selectedProducer.city}, {selectedProducer.county}
                        </div>
                      </div>
                      {selectedProducer.rating && (
                        <Badge className="bg-yellow-100 text-yellow-800 text-lg px-3 py-1">
                          <Star className="w-4 h-4 mr-1 fill-current" />
                          {selectedProducer.rating}
                        </Badge>
                      )}
                    </div>
                    
                    <p className="text-[#4a5568] mb-6">{selectedProducer.description}</p>
                    
                    {/* Certifications */}
                    {selectedProducer.certifications?.length > 0 && (
                      <div className="mb-6">
                        <h4 className="font-medium text-[#1A4D2E] mb-2">Certificări</h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedProducer.certifications.map((cert, i) => (
                            <Badge key={i} className="bg-green-100 text-green-800">
                              ✓ {cert}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Products */}
                    {selectedProducer.products?.length > 0 && (
                      <div className="mb-6">
                        <h4 className="font-medium text-[#1A4D2E] mb-2">Produse</h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedProducer.products.map((product, i) => (
                            <Badge key={i} variant="outline" className="border-[#1A4D2E]/30">
                              {product}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Delivery Areas */}
                    {selectedProducer.delivery && selectedProducer.delivery_areas?.length > 0 && (
                      <div className="mb-6">
                        <h4 className="font-medium text-[#1A4D2E] mb-2 flex items-center">
                          <Truck className="w-4 h-4 mr-2" />
                          Zone de Livrare
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedProducer.delivery_areas.map((area, i) => (
                            <Badge key={i} className="bg-[#F5F5F0] text-[#4a5568]">{area}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Contact */}
                    <div className="bg-[#F5F5F0] rounded-xl p-4 mb-6">
                      <h4 className="font-medium text-[#1A4D2E] mb-3">Contact</h4>
                      <div className="space-y-2 text-sm">
                        {selectedProducer.contact_phone && (
                          <p className="flex items-center text-[#4a5568]">
                            <Phone className="w-4 h-4 mr-2" />
                            {selectedProducer.contact_phone}
                          </p>
                        )}
                        {selectedProducer.website && (
                          <p className="flex items-center">
                            <Globe className="w-4 h-4 mr-2 text-[#4a5568]" />
                            <a 
                              href={selectedProducer.website} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-[#1A4D2E] hover:underline"
                            >
                              {selectedProducer.website}
                            </a>
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex gap-3">
                      <Button 
                        className="flex-1 bg-[#1A4D2E] text-white"
                        onClick={() => window.open(`tel:${selectedProducer.contact_phone}`, '_self')}
                      >
                        <Phone className="w-4 h-4 mr-2" />
                        Sună Acum
                      </Button>
                      {selectedProducer.website && (
                        <Button 
                          variant="outline"
                          className="flex-1 border-[#1A4D2E] text-[#1A4D2E]"
                          onClick={() => window.open(selectedProducer.website, '_blank')}
                        >
                          <Globe className="w-4 h-4 mr-2" />
                          Vizitează Site
                        </Button>
                      )}
                    </div>
                    
                    <Button 
                      variant="ghost" 
                      className="w-full mt-4"
                      onClick={() => setSelectedProducer(null)}
                    >
                      Închide
                    </Button>
                  </div>
                </motion.div>
              </div>
            )}

            {/* Info Section */}
            <Card className="border-none card-shadow mt-12">
              <CardContent className="p-8">
                <h3 className="font-serif text-xl text-[#1A4D2E] mb-4 text-center">
                  De Ce Să Cumperi Local?
                </h3>
                <div className="grid md:grid-cols-4 gap-6 text-center">
                  <div>
                    <div className="w-12 h-12 bg-[#1A4D2E]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Leaf className="w-6 h-6 text-[#1A4D2E]" />
                    </div>
                    <p className="font-medium text-[#1A4D2E]">Proaspăt</p>
                    <p className="text-sm text-[#4a5568]">Produse culese recent, fără transport lung</p>
                  </div>
                  <div>
                    <div className="w-12 h-12 bg-[#1A4D2E]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Star className="w-6 h-6 text-[#1A4D2E]" />
                    </div>
                    <p className="font-medium text-[#1A4D2E]">Calitate</p>
                    <p className="text-sm text-[#4a5568]">Produse naturale, fără chimicale</p>
                  </div>
                  <div>
                    <div className="w-12 h-12 bg-[#1A4D2E]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                      <ShoppingBag className="w-6 h-6 text-[#1A4D2E]" />
                    </div>
                    <p className="font-medium text-[#1A4D2E]">Economie</p>
                    <p className="text-sm text-[#4a5568]">Susții economia locală</p>
                  </div>
                  <div>
                    <div className="w-12 h-12 bg-[#1A4D2E]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                      🌍
                    </div>
                    <p className="font-medium text-[#1A4D2E]">Ecologic</p>
                    <p className="text-sm text-[#4a5568]">Amprentă de carbon redusă</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Producers;
